// Single source of truth for the three index-page list queries.
//
// The /[lang]/events, /[lang]/tours and /[lang]/articles pages are seeded at
// export time (see the app's src/lib/detail/list-prefetch.ts) so their cards ship as
// real <a href> in the static HTML rather than appearing only after
// hydration. That only works if the build-time fetch and the client hook
// issue *identical* queries and agree on the cache key — otherwise the first
// client render differs from the baked HTML and React throws a hydration
// mismatch (#418).
//
// So both sides import the argument builders and the query constants from
// here. Nothing in this module may depend on React or on the current time in a
// way that leaks into a key: `untilDays` is a stable number and the concrete
// date is derived inside the argument builder, at call time.

import type { BoatsQuery } from './hooks/use-boats';
import type { EventsQuery } from './hooks/use-events';
import type { ToursQuery } from './hooks/use-tours';
import type { Locale } from './i18n/locales';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Default forward window for EVENTS_RPC when a query doesn't set one. */
export const DEFAULT_EVENTS_WINDOW_DAYS = 7;

/**
 * Every events list (hubs, city pages, carousels) reads shows, not dates: one
 * row per recurring show — its soonest date in the window — with `date_count`
 * (see 20261001130000_event_shows.sql). Shared by useEvents and the seed.
 */
export const EVENTS_RPC = 'event_shows';

/** Arguments for EVENTS_RPC. Shared by useEvents and the seed. */
export function eventsRpcArgs(query: EventsQuery) {
  const from = query.from ?? new Date();
  const until =
    query.until ?? new Date(from.getTime() + (query.untilDays ?? DEFAULT_EVENTS_WINDOW_DAYS) * DAY_MS);
  return {
    p_lat: query.lat ?? null,
    p_lng: query.lng ?? null,
    p_radius_m: query.radiusM ?? 30000,
    p_from: from.toISOString(),
    p_until: until.toISOString(),
    p_category: query.category ?? null,
    p_city: query.city ?? null,
    p_limit: query.limit ?? 50,
  };
}

/**
 * Which RPC a tours query calls: `tours_by_theme` when it's scoped to a theme
 * (the /wine-tours pages), `tours_by_city` otherwise. Shared by useTours and
 * the seed, together with toursRpcArgs.
 */
export function toursRpcName(query: ToursQuery): 'tours_by_city' | 'tours_by_theme' {
  return query.theme ? 'tours_by_theme' : 'tours_by_city';
}

/** Arguments for toursRpcName(query). Shared by useTours and the seed. */
export function toursRpcArgs(query: ToursQuery) {
  return {
    ...(query.theme ? { p_theme: query.theme } : {}),
    p_city: query.city ?? null,
    p_limit: query.limit ?? 30,
  };
}

/** Arguments for the `boats_by_city` RPC. Shared by useBoats and the seed. */
export function boatsRpcArgs(query: BoatsQuery) {
  return {
    p_city: query.city ?? null,
    p_limit: query.limit ?? 30,
  };
}

// ---------------------------------------------------------------------------
// Index-page queries. These are the exact objects both the page and the seed
// pass, so the derived key matches on both sides.
// ---------------------------------------------------------------------------

/**
 * Cards per page on the /events, /tours and /boats hubs: the first page is
 * baked at build time (the *_INDEX_QUERY seeds below), every further page is
 * fetched in the browser by "Show more" — see hub-pages.ts.
 *
 * The index seeds are one page, not the whole catalogue: they're also baked
 * into the home page (carousels show 10) and read by the detail pages'
 * "related" blocks (4), and crawl coverage of every event/tour/boat comes from
 * the per-city pages, which list their city in full.
 */
export const HUB_PAGE_SIZE = 24;

/**
 * The events index pages through a season's worth of what's on (soonest
 * first), not the homepage's 7-day carousel window.
 */
export const EVENTS_INDEX_QUERY: EventsQuery = { limit: HUB_PAGE_SIZE, untilDays: 180 };

export const TOURS_INDEX_QUERY: ToursQuery = { limit: HUB_PAGE_SIZE };

export const BOATS_INDEX_QUERY: BoatsQuery = { limit: HUB_PAGE_SIZE };

/** Bucket used for baked list seeds, alongside the per-record detail buckets. */
export const LIST_BUCKET = 'list';

export const EVENTS_INDEX_SEED_KEY = 'events-index';
export const TOURS_INDEX_SEED_KEY = 'tours-index';
export const BOATS_INDEX_SEED_KEY = 'boats-index';

/**
 * A city landing page's own events/tours sections — narrower than the full
 * hub query (it's a page section, not the crawlable hub itself) but still
 * seeded at export time for the same reason: the cards need to be real links
 * in the page's static HTML, not something that only appears after hydration.
 */
export function cityEventsQuery(cityId: string): EventsQuery {
  return { city: cityId, limit: 24, untilDays: 180 };
}

export function cityToursQuery(cityId: string): ToursQuery {
  return { city: cityId, limit: 24 };
}

export function cityBoatsQuery(cityId: string): BoatsQuery {
  return { city: cityId, limit: 24 };
}

/**
 * The wine-tour landing pages (/[lang]/wine-tours and /[lang]/wine-tours/[city]).
 * The hub lists the whole region's wine tours, the city pages one departure
 * city's. Both are seeded at export time like the other hubs.
 */
export const WINE_THEME = 'wine';

export const WINE_TOURS_INDEX_QUERY: ToursQuery = { theme: WINE_THEME, limit: 100 };

export function wineToursCityQuery(cityId: string): ToursQuery {
  return { theme: WINE_THEME, city: cityId, limit: 60 };
}

export const WINE_TOURS_INDEX_SEED_KEY = 'wine-tours-index';

export function wineToursCitySeedKey(cityId: string): string {
  return `wine-tours-city:${cityId}`;
}

/**
 * The boat-rental landing pages (/[lang]/boat-rental/[city]) list every boat in
 * the city — the limit is a ceiling above the largest city (~260), not a
 * slice — so each boat detail page has at least one crawlable inbound link.
 */
export function boatRentalCityQuery(cityId: string): BoatsQuery {
  return { city: cityId, limit: 1000 };
}

/**
 * Per locale, unlike the other boat seeds: the page bakes every boat in the city
 * (~260 in Cannes), so the seed keeps only that locale's title — see
 * trimBoatForRentalPage in the app's list-prefetch.ts.
 */
export function boatRentalCitySeedKey(cityId: string, locale: Locale): string {
  return `boat-rental-city:${cityId}:${locale}`;
}

/** Precomputed BoatCityStats for one city page (see the app's src/lib/boat-rental.ts). */
export function boatRentalStatsSeedKey(cityId: string): string {
  return `boat-rental-stats:${cityId}`;
}

/** City ids with their own boat-rental page — same role as WINE_TOUR_CITIES_SEED_KEY. */
export const BOAT_RENTAL_CITIES_SEED_KEY = 'boat-rental-cities';

/**
 * One /boat-rental/[city]/[type] page's boats, per locale like
 * boatRentalCitySeedKey (same trimBoatForRentalPage rows, filtered to the type).
 */
export function boatTypePageSeedKey(cityId: string, type: string, locale: Locale): string {
  return `boat-type-page:${cityId}:${type}:${locale}`;
}

/** Precomputed BoatCityStats over one type page's boats (prices, ports). */
export function boatTypeStatsSeedKey(cityId: string, type: string): string {
  return `boat-type-stats:${cityId}:${type}`;
}

/** BoatTypePages (city id → exported type pages) — so no link names an unexported page. */
export const BOAT_TYPE_PAGES_SEED_KEY = 'boat-type-pages';

/**
 * Seed key for the list of city ids that get their own wine-tour page — the
 * hub's "by departure city" links must name exactly the pages
 * generateWineCityStaticParams exported, so the hub bakes the list it was
 * rendered with instead of re-deriving it on the client.
 */
export const WINE_TOUR_CITIES_SEED_KEY = 'wine-tour-cities';

/**
 * Every per-city page list (boat rental, wine tours above; events and tours —
 * CITY_LISTING_PAGES_SEED_KEY), combined for the site footer. The footer is on
 * every page, so it bakes this one small seed itself rather than relying on
 * the page to have baked the separate ones.
 */
export const FOOTER_LINKS_SEED_KEY = 'footer-links';

export type FooterCityLinks = {
  boatRental: string[];
  wineTours: string[];
  /** Cities with an exported /[city]/events and /[city]/tours page (CityListingPages). */
  cityEvents: string[];
  cityTours: string[];
};

/**
 * The per-city listing pages (/[lang]/[city]/events and /[lang]/[city]/tours)
 * list the city in full — every upcoming event, every tour — so each detail
 * page has a crawlable inbound link from its city's page. The limits are
 * ceilings above the largest city (~200 events in Nice), not slices. Events
 * look two years ahead: the feed sells theatre seasons well beyond the
 * 180-day window the hubs use.
 */
export function cityEventsPageQuery(cityId: string): EventsQuery {
  return { city: cityId, limit: 1000, untilDays: 730 };
}

export function cityToursPageQuery(cityId: string): ToursQuery {
  return { city: cityId, limit: 1000 };
}

/**
 * Per locale, like boatRentalCitySeedKey: the events page bakes the whole city
 * (~200 rows in Nice), so the seed keeps only that locale's title — see
 * trimEventForCityPage in the app's list-prefetch.ts.
 */
export function cityEventsPageSeedKey(cityId: string, locale: Locale): string {
  return `city-events-page:${cityId}:${locale}`;
}

export function cityToursPageSeedKey(cityId: string): string {
  return `city-tours-page:${cityId}`;
}

/**
 * The CityListingPages (city-listings.ts) the export generated pages
 * for — same role as WINE_TOUR_CITIES_SEED_KEY, so a link to /[city]/events or
 * /[city]/tours can only ever name an exported page.
 */
export const CITY_LISTING_PAGES_SEED_KEY = 'city-listing-pages';

/**
 * Event category ids with at least one upcoming show anywhere (in `categories`
 * order), baked at export for the /events hub's category filter, so it offers
 * no option that can only come back empty. Built from the /[city]/events
 * lists (every city, two years ahead) — a slight superset of the hub's window.
 */
export const EVENT_CATEGORIES_SEED_KEY = 'event-categories';

/**
 * The CityEventCategoryPages (the app's src/lib/city-event-slices.ts) the export
 * generated /[city]/events/[category] pages for, so a link to one can only
 * name an exported page. The window pages (/[city]/events/today…) need no
 * list: every city with a /[city]/events page has all three.
 */
export const CITY_EVENT_CATEGORY_PAGES_SEED_KEY = 'city-event-category-pages';

/** A /[city]/events/[slice] page's EventSliceSeed, per locale (each keeps only its own titles). */
export function cityEventSliceSeedKey(cityId: string, slice: string, locale: Locale): string {
  return `city-event-slice:${cityId}:${slice}:${locale}`;
}

export function cityEventsSeedKey(cityId: string): string {
  return `city-events:${cityId}`;
}

export function cityToursSeedKey(cityId: string): string {
  return `city-tours:${cityId}`;
}

export function cityBoatsSeedKey(cityId: string): string {
  return `city-boats:${cityId}`;
}

/**
 * The "tours & boats in this city" block (CityCrossSell) on the event and
 * article pages — the pages that get the search traffic. The top few of each
 * catalogue for the city (region-wide for a page with no known city), in the
 * same order as the city/hub lists. Its own small seeds, sliced from those
 * lists at export, so a page bakes a handful of rows rather than the 24-row
 * city seeds; they're a separate key from the city seeds so a page that bakes
 * the slice can never pass it off as the full list after a client navigation.
 */
export const CROSS_SELL_LIMIT = 4;

export function crossSellToursQuery(cityId: string | null): ToursQuery {
  return cityId ? { city: cityId, limit: CROSS_SELL_LIMIT } : { limit: CROSS_SELL_LIMIT };
}

export function crossSellBoatsQuery(cityId: string | null): BoatsQuery {
  return cityId ? { city: cityId, limit: CROSS_SELL_LIMIT } : { limit: CROSS_SELL_LIMIT };
}

export function crossSellToursSeedKey(cityId: string | null): string {
  return `cross-sell-tours:${cityId ?? 'region'}`;
}

export function crossSellBoatsSeedKey(cityId: string | null): string {
  return `cross-sell-boats:${cityId ?? 'region'}`;
}

/**
 * Articles differ per locale (separate Sanity documents, unlike the
 * region-wide events/tours RPCs), so their seed key carries the locale rather
 * than being one constant shared by all three languages.
 */
export function articlesIndexSeedKey(locale: Locale): string {
  return `articles-index:${locale}`;
}
